import { enrichGuardianDiffHierarchy } from "./enrichGuardianDiffHierarchy";
import { mergeStandards } from "./standardMerger";
import { isParentFacingRating } from "./teacher/InputRatingUtils";

const nodeHasParentFacingRating = (node) =>
    isParentFacingRating(node?.contentRating?.rating) || isParentFacingRating(node?.diffRating?.rating);

const eoHasParentFacingRating = (eo) =>
    nodeHasParentFacingRating(eo) || (eo.evidenceOutcomeChildren || []).some(nodeHasParentFacingRating);

const gleHasParentFacingRating = (gle) =>
    nodeHasParentFacingRating(gle) || (gle.evidenceOutcomes || []).some(eoHasParentFacingRating);

export const mergedStandardsHaveParentFacingRatings = (standards) =>
    (standards || []).some((standard) => (standard.gles || []).some(gleHasParentFacingRating));

export const mergeSubjectStandards = (rawData, subjectName, extra = {}) => {
    const filteredData = (rawData || []).filter((criteria) => criteria.subjectName === subjectName);
    // Fills null grades / labels so 7th–8th diffs stay visible in parent preview.
    const enriched = enrichGuardianDiffHierarchy(filteredData, extra);
    return mergeStandards(enriched);
};

export const subjectHasParentFacingRatings = (rawData, subjectName, extra = {}) =>
    mergedStandardsHaveParentFacingRatings(mergeSubjectStandards(rawData, subjectName, extra));

/**
 * True when customData has at least one non-empty value (string JSON or object).
 * Keeps subjects visible when the only parent-facing content is customData
 * (e.g. readingFluency today; other keys later) with no content ratings.
 */
export const hasParentFacingCustomData = (customData) => {
    let data = customData;
    if (typeof data === "string") {
        try {
            data = JSON.parse(data);
        } catch {
            return false;
        }
    }
    if (!data || typeof data !== "object" || Array.isArray(data)) return false;

    return Object.values(data).some((value) => {
        if (value == null) return false;
        if (typeof value === "string") return value.trim() !== "";
        if (typeof value === "object") return Object.keys(value).length > 0;
        return true;
    });
};

export const subjectHasParentFacingCustomData = (rawData, subjectName) => {
    const criteria = (rawData || []).find((row) => row.subjectName === subjectName);
    return hasParentFacingCustomData(criteria?.customData);
};

export const parentFacingSubjectNames = (gradingCriteriaDtos, extra = {}) => {
    const { reportableContent, diffRatings } = extra;
    const subjects = (gradingCriteriaDtos || []).map((criteria) => ({
        subject: criteria.subjectName,
        ordinal: criteria.ordinal
    }));
    subjects.sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));
    const unique = [...new Set(subjects.map((s) => s.subject).filter(Boolean))];
    return unique.filter(
        (subjectName) =>
            subjectHasParentFacingRatings(gradingCriteriaDtos, subjectName, { reportableContent, diffRatings }) ||
            subjectHasParentFacingCustomData(gradingCriteriaDtos, subjectName)
    );
};
